<script setup>
import { computed } from 'vue'

const props = defineProps({
  recommendations: { type: Object, required: true },
  sipAmount: { type: Number, required: true },
  loading: { type: Boolean, default: false }
})

const emit = defineEmits(['save'])

const anchors = computed(() => props.recommendations?.anchors || [])
const satellites = computed(() => props.recommendations?.satellites || [])
const allFunds = computed(() => [...anchors.value, ...satellites.value])

const totalAllocation = computed(() => {
  return allFunds.value.reduce((sum, fund) => sum + (fund.allocationPercent || 0), 0)
})

// Helper to calculate SIP amount from allocation percentage
const calculateSIPAmount = (allocationPercent) => {
  if (!props.sipAmount || !allocationPercent) return 0
  return Math.round((props.sipAmount * allocationPercent) / 100)
}

// Helper to format currency
const formatCurrency = (amount) => {
  return new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(amount)
}
</script>

<template>
  <div class="sip-recommendation">
    <div v-if="loading" class="loading-state">
      <div class="spinner" />
      <p>Generating your personalized portfolio...</p>
    </div>

    <template v-else>
      <div class="recommendation-header">
        <h3>Your Personalized SIP Portfolio</h3>
        <p class="header-subtitle">
          Monthly SIP: ₹{{ sipAmount.toLocaleString('en-IN') }}
        </p>
      </div>

      <div class="funds-section">
        <div class="section-title">
          <span class="title-badge anchor-badge">Anchor Funds</span>
          <span class="title-subtitle">Core holdings for stability</span>
        </div>
        <div class="funds-grid">
          <div
            v-for="(fund, idx) in anchors"
            :key="'anchor-' + idx"
            class="fund-card anchor-card"
          >
            <div class="fund-header">
              <h4>{{ fund.fundName || fund.name }}</h4>
              <span class="allocation-badge">{{ (fund.allocationPercent || 0).toFixed(1) }}%</span>
            </div>
            <p class="fund-category">{{ fund.category }}</p>
            <div class="fund-amount">
              ₹{{ formatCurrency(calculateSIPAmount(fund.allocationPercent)) }}/month
            </div>
            <div class="fund-percentage">
              {{ fund.allocationPercent }}% of total SIP
            </div>
            <p class="fund-rationale">{{ fund.why }}</p>
            <div class="fund-metrics">
              <span v-if="fund.risk">Risk: {{ fund.risk }}</span>
              <span v-if="fund.sharpe3Y">Sharpe: {{ fund.sharpe3Y }}</span>
            </div>
          </div>
        </div>
      </div>

      <div v-if="satellites.length > 0" class="funds-section">
        <div class="section-title">
          <span class="title-badge satellite-badge">Satellite Funds</span>
          <span class="title-subtitle">Tactical allocations for enhancement</span>
        </div>
        <div class="funds-grid">
          <div
            v-for="(fund, idx) in satellites"
            :key="'satellite-' + idx"
            class="fund-card satellite-card"
          >
            <div class="fund-header">
              <h4>{{ fund.fundName || fund.name }}</h4>
              <span class="allocation-badge">{{ (fund.allocationPercent || 0).toFixed(1) }}%</span>
            </div>
            <p class="fund-category">{{ fund.category }}</p>
            <div class="fund-amount">
              ₹{{ formatCurrency(calculateSIPAmount(fund.allocationPercent)) }}/month
            </div>
            <div class="fund-percentage">
              {{ fund.allocationPercent }}% of total SIP
            </div>
            <p class="fund-rationale">{{ fund.why }}</p>
            <div class="fund-metrics">
              <span v-if="fund.risk">Risk: {{ fund.risk }}</span>
              <span v-if="fund.sharpe3Y">Sharpe: {{ fund.sharpe3Y }}</span>
            </div>
          </div>
        </div>
      </div>

      <div class="action-section">
        <button class="btn-primary btn-save" @click="emit('save')">
          <i class="pi pi-save" style="margin-right: 8px;" />
          Save to My Portfolio
        </button>
      </div>
    </template>
  </div>
</template>

<style scoped>
.sip-recommendation {
  display: flex;
  flex-direction: column;
  gap: 24px;
}

.loading-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 20px;
  gap: 16px;
}

.spinner {
  width: 40px;
  height: 40px;
  border: 4px solid #e9ecef;
  border-top-color: var(--an-primary);
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

.recommendation-header {
  text-align: center;
  padding: 20px;
  background: linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%);
  border-radius: 8px;
}

.recommendation-header h3 {
  margin: 0 0 8px 0;
  color: var(--an-primary);
  font-size: 1.4rem;
}

.header-subtitle {
  margin: 0;
  font-size: 1.1rem;
  color: var(--an-text-primary);
  font-weight: 600;
}

.funds-section {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.section-title {
  display: flex;
  align-items: center;
  gap: 12px;
}

.title-badge {
  padding: 6px 14px;
  border-radius: 999px;
  font-size: 0.85rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.anchor-badge {
  background: #dbeafe;
  color: #1e40af;
}

.satellite-badge {
  background: #dcfce7;
  color: #15803d;
}

.title-subtitle {
  font-size: 0.9rem;
  color: #868e96;
  font-style: italic;
}

.funds-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 16px;
}

.fund-card {
  padding: 18px;
  border-radius: 8px;
  display: flex;
  flex-direction: column;
  gap: 10px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
}

.anchor-card {
  background: linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%);
  border-left: 4px solid #3b82f6;
}

.satellite-card {
  background: linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%);
  border-left: 4px solid #22c55e;
}

.fund-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 12px;
}

.fund-header h4 {
  margin: 0;
  font-size: 1rem;
  color: var(--an-text-primary);
  flex: 1;
}

.allocation-badge {
  padding: 4px 10px;
  background: rgba(0, 0, 0, 0.08);
  border-radius: 999px;
  font-size: 0.85rem;
  font-weight: 700;
  white-space: nowrap;
}

.fund-category {
  margin: 0;
  font-size: 0.85rem;
  color: #868e96;
  font-weight: 500;
}

.fund-amount {
  font-size: 1.15rem;
  font-weight: 700;
  color: var(--an-primary);
}

.fund-percentage {
  font-size: 0.9rem;
  color: #495057;
}

.fund-rationale {
  margin: 0;
  font-size: 0.88rem;
  color: var(--an-text-secondary);
  line-height: 1.4;
}

.fund-metrics {
  display: flex;
  gap: 16px;
  padding-top: 8px;
  border-top: 1px solid rgba(0, 0, 0, 0.1);
  font-size: 0.8rem;
  color: #495057;
}

.action-section {
  display: flex;
  justify-content: center;
  padding: 24px 0;
}

.btn-save {
  padding: 12px 32px;
  font-size: 1rem;
}

.btn-primary {
  background: var(--an-primary);
  color: white;
  border: none;
  border-radius: 6px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  align-items: center;
}

.btn-primary:hover {
  background: #0a6454;
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}

@media (max-width: 768px) {
  .funds-grid {
    grid-template-columns: 1fr;
  }
}
</style>
